/* verqo — buyer nav config (preview build · Feb 2026)
   Mirror of seller nav, buyer-side.
   Iter Feb-B · Icons audited against buyer portal (PortalShell.jsx).
     - RFQ              → MessageSquare (portal /buyer/rfq)
     - Credit terms     → Landmark (portal Payments > Credit tab)
     - Trade score      → TrendingUp (portal /buyer/score)
     - Credit dashboard → PieChart (portal Payments > Overview)
     - Financing        → Banknote (portal Hub > Invoice financing)
     - Insurance        → Shield (matches seller portal /seller/insurance)
   Buyer product routes live under /preview/nav/buyers/*.
   Company links intentionally reuse the shared /preview/nav/company/* URLs.
*/
import {
  Search, Layers3, MessageSquare, RotateCw,
  Landmark, TrendingUp, PieChart,
  Wallet, Zap, Banknote, Split,
  Truck, Shield,
  Building2, Store, Network, ShoppingBag,
  Sparkles, Shirt, UtensilsCrossed, Home, Heart, ShoppingBasket,
  LineChart, Briefcase, Boxes as BoxesIcon,
} from "lucide-react";

const P = (slug) => `/buyers/product/${slug}`;
const S = (slug) => `/buyers/solutions/${slug}`;
const I = (slug) => `/buyers/solutions/industry/${slug}`;
const R = (slug) => `/buyers/solutions/role/${slug}`;
const C = (slug) => `/company/${slug}`;

export const BUYER_NAV = [
  {
    id: "product",
    label: "Product",
    mega: true,
    featured: [
      { slug: "shipping-standalone", label: "Verqo Shipping", tag: "Standalone", desc: "Print labels & book freight for all your sales channels from one hub — a cheaper ShipStation replacement, works without B2B orders.", href: P("ship"), icon: Truck },
      { slug: "banking", label: "Verqo Wallet & Banking", tag: "Core", desc: "One wallet for every supplier — ACH, wires, cards, and auto-debit on due dates.", href: P("pay"), icon: Landmark },
    ],
    columns: [
      {
        heading: "Discover",
        items: [
          { label: "Discover",       slug: "discover",       href: P("discover"),       desc: "One search across every wholesale supplier.", icon: Search },
          { label: "Multi-supplier", slug: "multi-supplier", href: P("multi-supplier"), desc: "One cart, many brands, one PO.",              icon: Layers3 },
          { label: "RFQ",            slug: "rfq",            href: P("rfq"),            desc: "Send one RFQ, get parallel bids back.",       icon: MessageSquare },
          { label: "Reorder",        slug: "reorder",        href: P("reorder"),        desc: "Auto-reorder your best-sellers on schedule.", icon: RotateCw },
        ],
      },
      {
        heading: "Get terms",
        items: [
          { label: "Credit terms",     slug: "credit-terms",     href: P("credit-terms"),     desc: "Net-30/60/90 across every supplier in one click.", icon: Landmark },
          { label: "Trade score",      slug: "trade-score",      href: P("trade-score"),      desc: "Your buy-side reputation, portable.",              icon: TrendingUp },
          { label: "Credit dashboard", slug: "credit-dashboard", href: P("credit-dashboard"), desc: "Every open invoice + due date in one view.",       icon: PieChart },
        ],
      },
      {
        heading: "Pay your way",
        items: [
          { label: "Pay",         slug: "pay",         href: P("pay"),         desc: "ACH, card, wire — one wallet.",                     icon: Wallet },
          { label: "Auto-debit",  slug: "auto-debit",  href: P("auto-debit"),  desc: "Never miss a due date. Never pay a late fee.",      icon: Zap },
          { label: "Financing",   slug: "financing",   href: P("financing"),   desc: "Extend terms up to 120 days when cash is tight.",   icon: Banknote },
          { label: "Split pay",   slug: "split-pay",   href: P("split-pay"),   desc: "Split any invoice across 3 or 6 monthly payments.", icon: Split },
        ],
      },
      {
        heading: "Ship & protect",
        items: [
          { label: "Ship",      slug: "ship",      href: P("ship"),      desc: "Consolidated freight from every supplier.",  icon: Truck },
          { label: "Insurance", slug: "insurance", href: P("insurance"), desc: "Buyer-side cover on every dropship parcel.", icon: Shield },
        ],
      },
    ],
  },
  {
    id: "solutions",
    label: "Solutions",
    mega: true,
    columns: [
      {
        heading: "By business type",
        items: [
          { label: "Retailers",    slug: "retailers",    href: S("retailers"),    desc: "Independent boutiques and specialty retailers.", icon: Store },
          { label: "Chains",       slug: "chains",       href: S("chains"),       desc: "Multi-location and regional chains.",           icon: Building2 },
          { label: "Marketplaces", slug: "marketplaces", href: S("marketplaces"), desc: "E-com marketplaces sourcing at wholesale.",     icon: Network },
          { label: "Procurement",  slug: "procurement",  href: S("procurement"),  desc: "Corporate & institutional procurement teams.",  icon: ShoppingBag },
        ],
      },
      {
        heading: "By role · in retail buying",
        items: [
          { label: "Founders & Owners",   slug: "founder-owner",     href: R("founder-owner"),     desc: "For independent retailers running the shop and the buy.", icon: Briefcase },
          { label: "CFOs & Finance",      slug: "cfo",               href: R("cfo"),               desc: "For finance leaders at retailers/chains extending vendor terms cleanly.", icon: LineChart },
          { label: "Head of Buying",      slug: "head-of-buying",    href: R("head-of-buying"),    desc: "For the person owning the wholesale purchase plan.", icon: Wallet },
          { label: "Ops & Store Managers",slug: "ops-manager",       href: R("ops-manager"),       desc: "For the people running reorder cadence and receiving.", icon: BoxesIcon },
        ],
      },
      {
        heading: "By industry",
        items: [
          { label: "Beauty",   slug: "beauty",   href: I("beauty"),   desc: "Beauty & personal care buying.",   icon: Sparkles },
          { label: "Apparel",  slug: "apparel",  href: I("apparel"),  desc: "Fashion & accessories buying.",    icon: Shirt },
          { label: "Food",     slug: "food",     href: I("food"),     desc: "Food, coffee, and beverage.",       icon: UtensilsCrossed },
          { label: "Home",     slug: "home",     href: I("home"),     desc: "Home & lifestyle goods.",           icon: Home },
          { label: "Health",   slug: "health",   href: I("health"),   desc: "Wellness & health retailers.",      icon: Heart },
          { label: "CPG",      slug: "cpg",      href: I("cpg"),      desc: "CPG resellers at every scale.",     icon: ShoppingBasket },
        ],
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    href: "/buyers/pricing",
  },
  {
    id: "company",
    label: "Company",
    mega: true,
    columns: [
      {
        heading: "About Verqo",
        items: [
          { label: "Our story",    slug: "story",         href: C("story"),         desc: "Why we built Verqo — from a warehouse floor." },
          { label: "Why Verqo",    slug: "why",           href: C("why"),           desc: "The wholesale-OS thesis in 90 seconds." },
          { label: "How it works", slug: "how-it-works",  href: C("how-it-works"),  desc: "The six-player ledger, explained." },
          { label: "Team",         slug: "team",          href: C("team"),          desc: "Operators, engineers, wholesalers." },
          { label: "Careers",      slug: "careers",       href: C("careers"),       desc: "We're hiring across every function." },
          { label: "Press",        slug: "press",         href: C("press"),         desc: "News, coverage, and press kit." },
        ],
      },
      {
        heading: "Trust & partners",
        items: [
          { label: "Partners", slug: "partners", href: C("partners"), desc: "Banks, insurers, integrators, resellers." },
          { label: "Security", slug: "security", href: C("security"), desc: "SOC 2, bank-grade encryption, examiner-ready." },
          { label: "Privacy",  slug: "privacy",  href: C("privacy"),  desc: "How we handle your data, plainly stated." },
          { label: "Contact",  slug: "contact",  href: C("contact"),  desc: "Talk to a real wholesaler-turned-founder." },
          { label: "Status",   slug: "status",   href: C("status"),   desc: "Live uptime and incident history." },
        ],
      },
    ],
  },
];
